import { STORAGE_KEYS } from '../constants';
import type { BiomeType } from '../systems/LevelLoader';

// ============================================
// GameState (Step 11 Integration)
// ============================================

export type GameMode = '1P' | '2P' | '3P' | '4P';

// Experiment mode settings
interface ExperimentSettings {
  enabled: boolean;
  unlimitedResources: boolean;
  allTowersUnlocked: boolean;
  villagerCount: number;
  selectedBiome: BiomeType;
}

interface GameSession {
  scenarioId: string;
  mode: GameMode;
  currentWave: number;
  totalWaves: number;
  monstersKilled: number;
  monsterDamageDealt: number;  // Total HP of killed monsters (not leaked)
  towersBuilt: number;
  elapsedTime: number;
  startTime: number;
  isPaused: boolean;
  isGameOver: boolean;
  isVictory: boolean;
  waveSkipBonuses: number;  // Count of valid wave skips (>5s early)
}

class GameStateManager {
  private mode: GameMode = '1P';
  private selectedScenario: string = 'F1';
  private session: GameSession | null = null;
  private experimentSettings: ExperimentSettings = {
    enabled: false,
    unlimitedResources: true,
    allTowersUnlocked: true,
    villagerCount: 100,
    selectedBiome: 'forest',
  };

  // ============================================
  // Mode Management
  // ============================================

  setMode(mode: GameMode): void {
    this.mode = mode;
    console.log(`[GameState] Mode set to: ${mode}`);
  }

  getMode(): GameMode {
    return this.mode;
  }

  is2Player(): boolean {
    return this.mode === '2P';
  }

  is3Player(): boolean {
    return this.mode === '3P';
  }

  is4Player(): boolean {
    return this.mode === '4P';
  }

  getPlayerCount(): number {
    switch (this.mode) {
      case '2P': return 2;
      case '3P': return 3;
      case '4P': return 4;
      default: return 1;
    }
  }

  // ============================================
  // Scenario Selection
  // ============================================

  setSelectedScenario(scenarioId: string): void {
    this.selectedScenario = scenarioId;
    console.log(`[GameState] Selected scenario: ${scenarioId}`);
  }

  getSelectedScenario(): string {
    return this.selectedScenario;
  }

  // ============================================
  // Game Session
  // ============================================

  startNewGame(scenarioId: string, totalWaves: number): void {
    this.session = {
      scenarioId,
      mode: this.mode,
      currentWave: 0,
      totalWaves,
      monstersKilled: 0,
      monsterDamageDealt: 0,
      towersBuilt: 0,
      elapsedTime: 0,
      startTime: Date.now(),
      isPaused: false,
      isGameOver: false,
      isVictory: false,
      waveSkipBonuses: 0,
    };
    console.log(`[GameState] New game started: ${scenarioId} (${this.mode})`);
  }

  getSession(): GameSession {
    if (!this.session) {
      // Return empty session if none exists
      return {
        scenarioId: '',
        mode: '1P',
        currentWave: 0,
        totalWaves: 0,
        monstersKilled: 0,
        monsterDamageDealt: 0,
        towersBuilt: 0,
        elapsedTime: 0,
        startTime: 0,
        isPaused: false,
        isGameOver: false,
        isVictory: false,
        waveSkipBonuses: 0,
      };
    }
    return { ...this.session };
  }

  hasActiveSession(): boolean {
    return this.session !== null && !this.session.isGameOver;
  }

  // ============================================
  // Session Updates
  // ============================================

  setCurrentWave(wave: number): void {
    if (this.session) {
      this.session.currentWave = wave;
    }
  }

  addMonsterKill(): void {
    if (this.session) {
      this.session.monstersKilled++;
    }
  }

  /**
   * Track damage dealt when a monster is killed (not leaked)
   */
  addMonsterDamage(monsterHP: number): void {
    if (this.session) {
      this.session.monsterDamageDealt += monsterHP;
    }
  }

  /**
   * Track a valid wave skip (player advanced wave >5s before it would auto-start)
   */
  addWaveSkipBonus(): void {
    if (this.session) {
      this.session.waveSkipBonuses++;
    }
  }

  addTowerBuilt(): void {
    if (this.session) {
      this.session.towersBuilt++;
    }
  }

  setPaused(paused: boolean): void {
    if (this.session) {
      this.session.isPaused = paused;
    }
  }

  setGameOver(victory: boolean): void {
    if (this.session) {
      this.session.isGameOver = true;
      this.session.isVictory = victory;
      this.updateElapsedTime();
    }
  }

  updateElapsedTime(): void {
    if (this.session && !this.session.isPaused) {
      this.session.elapsedTime = (Date.now() - this.session.startTime) / 1000;
    }
  }

  // ============================================
  // Experiment Mode
  // ============================================

  startExperimentMode(): void {
    this.experimentSettings.enabled = true;
    console.log('[GameState] Experiment mode enabled');
  }

  endExperimentMode(): void {
    this.experimentSettings.enabled = false;
    console.log('[GameState] Experiment mode disabled');
  }

  isExperimentMode(): boolean {
    return this.experimentSettings.enabled;
  }

  getExperimentSettings(): ExperimentSettings {
    return { ...this.experimentSettings };
  }

  setSelectedBiome(biome: BiomeType): void {
    this.experimentSettings.selectedBiome = biome;
  }

  getSelectedBiome(): BiomeType {
    return this.experimentSettings.selectedBiome;
  }

  // ============================================
  // Reset
  // ============================================

  endSession(): void {
    this.session = null;
  }

  reset(): void {
    this.mode = '1P';
    this.selectedScenario = 'F1';
    this.session = null;
    this.experimentSettings.enabled = false;
  }
}

// Singleton instance
export const GameState = new GameStateManager();
